// src/pages/Tools/components/ToolPageLayout.tsx
// Replaces: .tool-page-wrapper, .tool-header-clean, .tool-header-content,
//           .tool-icon-badge, h1 inside header, .tool-subtitle,
//           .tool-trust-tags-center, .tool-container-clean

import React from 'react';
import { cn } from '../../../lib/utils';
import { Typography } from '../../../components/ui/typography';

interface ToolPageLayoutProps {
    /** Icon element displayed in the 72px badge above the title */
    icon?: React.ReactNode;
    /** Page h1 title */
    title: string;
    /** Subtitle paragraph below the title */
    subtitle?: string;
    /** Trust tags / badge row below subtitle (flex, centered, wraps) */
    tags?: React.ReactNode;
    /** Main body content (rendered inside .tool-container-clean) */
    children: React.ReactNode;
    className?: string;
}

/*
 * .tool-page-wrapper:
 *   max-width: 100%; margin: 0 auto; padding: 0;
 *   font-family: Inter; color: #202124;
 *   background: #f8f9fa; min-height: 100vh;
 *
 * .tool-header-clean:
 *   padding: 48px 24px 40px;
 *   background: linear-gradient(180deg, #fff 0%, #f8f9fa 100%);
 *   text-align: center; border-bottom: 1px solid #dadce0;
 *   (mobile: padding: 40px 16px 32px)
 *
 * .tool-header-content:
 *   max-width: 800px; margin: 0 auto;
 *   display: flex; flex-direction: column; align-items: center;
 *
 * .tool-icon-badge:
 *   display: inline-flex; align-items: center; justify-content: center;
 *   width: 72px; height: 72px;
 *   background: #e8f0fe; color: #1a73e8;
 *   border-radius: 50%; margin-bottom: 20px;
 *
 * h1 inside .tool-header-clean:
 *   font-size: 2.25rem (mobile: 1.75rem); font-weight: 400;
 *   color: #202124; margin: 0 0 16px; letter-spacing: -0.5px;
 *
 * .tool-subtitle:
 *   font-size: 1.125rem (mobile: 1rem); color: #5f6368; margin: 0;
 *   line-height: 1.7; max-width: 680px; margin-left/right: auto;
 *
 * .tool-trust-tags-center:
 *   display: flex; justify-content: center; gap: 12px;
 *   margin-top: 16px; flex-wrap: wrap;
 *
 * .tool-container-clean:
 *   max-width: 1200px; margin: 0 auto;
 *   padding: 40px 24px (mobile: 24px 16px);
 */
const ToolPageLayout: React.FC<ToolPageLayoutProps> = ({
    icon,
    title,
    subtitle,
    tags,
    children,
    className,
}) => (
    <div className={cn('min-h-screen bg-white font-brand text-[#202124]', className)}>

        {/* Header */}
        <header className={cn(
            'px-[24px] pt-[48px] pb-[40px]',
            'text-center',
            'max-sm:px-[16px] max-sm:pt-[40px] max-sm:pb-[32px]',
        )}>
            <div className="max-w-[800px] mx-auto flex flex-col items-center">


                <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                    {title}
                </Typography>

                {subtitle && (
                    <Typography variant="hero-subtitle" weight="normal" className="m-0 max-w-[680px] mx-auto text-[#5f6368]">
                        {subtitle}
                    </Typography>
                )}

                {tags && (
                    <div className="flex justify-center gap-[12px] mt-[16px] flex-wrap">
                        {tags}
                    </div>
                )}
            </div>
        </header>

        {/* Main container */}
        <div className="max-w-[1200px] mx-auto px-[24px] py-[40px] max-sm:px-[16px] max-sm:py-[24px]">
            {children}
        </div>
    </div>
);

export default ToolPageLayout;
